import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, Subscription } from 'rxjs';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';

import { pageTransitions } from '../../../../../../../shared/animations/page-transitions';
import { AddUserService } from '../../../../../../services/add-user.service';
import { AddUserPageContent } from '../../../../../../../shared/models/add-user-info';

@Component({
	selector: 'copo-step-user-info',
	templateUrl: './step-user-info.component.html',
	styleUrl: './step-user-info.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		FormsModule,
		ReactiveFormsModule,

		MatFormFieldModule,
		MatSelectModule,
		TranslateModule,

		MatInputModule,

		MatButtonModule,
		MatIconModule,

		MatCheckboxModule,
	],
})
export class StepUserInfoComponent extends AddUserPageContent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	userForm!: FormGroup;
	resizeSub?: Subscription;
	sub?: Subscription;
	fistName?: string;
	lastName?: string;
	email?: string;
	userType?: string;
	administrator?: boolean;
	isSubscriptionSelected = false;
	constructor(
		readonly router: Router,
		private readonly addUserService: AddUserService,
	) {
		super();
		this.userForm = new FormGroup({
			administrator: new FormControl(false, { nonNullable: true, validators: [Validators.required] }),
			firstName: new FormControl('', Validators.required),
			lastName: new FormControl('', Validators.required),
			email: new FormControl('', [
				Validators.required,
				Validators.pattern('^[a-zA-Z0-9._\'-]+@[a-zA-Z0-9.-]{2,}[.][a-zA-Z]{2,}$')
			]),
			userType: new FormControl('', Validators.required),
		});
		this.sub = this.addUserService.firstName
			.subscribe(firstName => {
				this.fistName = firstName;
			});
		this.sub = this.addUserService.lastName
			.subscribe(lastName => {
				this.lastName = lastName;
			});
		this.sub = this.addUserService.email
			.subscribe(email => {
				this.email = email;
			});
		this.sub = this.addUserService.userType
			.subscribe(userType => {
				this.userType = userType;
			});
		this.sub = this.addUserService.administrator
			.subscribe(administrator => {
				this.administrator = administrator;
			});
		if (this.fistName || this.lastName || this.email || this.administrator || this.userType) {
			this.userForm.get('firstName')?.patchValue(this.fistName);
			this.userForm.get('lastName')?.patchValue(this.lastName);
			this.userForm.get('email')?.patchValue(this.email);
			this.userForm.get('administrator')?.patchValue(this.administrator);
			this.userForm.get('userType')?.patchValue(this.userType);
			this.isSubscriptionSelected = this.userType === 'subscription';
			if (this.isSubscriptionSelected) {
				this.userForm.get('administrator')?.setValue(false);
				this.userForm.get('administrator')?.disable();
			}
		}
		this.userForm.get('firstName')?.valueChanges
			.pipe(debounceTime(500))
			.subscribe({
				next: (value: string) => {
					this.addUserService.updateFirstName(value);
					this.clear();
				}
			});
		this.userForm.get('lastName')?.valueChanges
			.pipe(debounceTime(500))
			.subscribe({
				next: (value: string) => {
					this.addUserService.updateLastName(value);
					this.clear();
				}
			});
		this.userForm.get('email')?.valueChanges
			.pipe(debounceTime(500))
			.subscribe({
				next: (value: string) => {
					this.addUserService.updateEmail(value);
					this.clear();
				}
			});
		this.userForm.get('userType')?.valueChanges
			.pipe(debounceTime(500))
			.subscribe({
				next: (value: string) => {
					this.addUserService.updateUserType(value);
					this.isSubscriptionSelected = value === 'subscription';
					if (this.isSubscriptionSelected) {
						this.userForm.get('administrator')?.setValue(false);
						this.userForm.get('administrator')?.disable();
					} else {
						this.userForm.get('administrator')?.enable();
					}
					this.clear();
				}
			});
		this.userForm.get('administrator')?.valueChanges
			.pipe(debounceTime(500))
			.subscribe({
				next: (value: boolean) => {
					this.addUserService.updateAdmin(value);
				}
			});
	}

	checkErrors(): boolean | undefined {
		this.userForm.markAllAsTouched();
		return this.userForm.valid;
	}

	ngOnDestroy() {
		if (this.sub !== undefined) {
			this.sub.unsubscribe();
		}
	}

	clear() {
		this.addUserService.updateIsAnyOneChoiceSelected(false);
		this.addUserService.setSelectedUser(undefined);
		this.addUserService.updateIsNewUserCreation(false);
		this.addUserService.updateNodeSelected(undefined);
		this.addUserService.updateServices([]);
	}
}
